
import React, { useEffect, useState, useMemo } from "react";

export default function Filtering({
  productId,
  availableFilters = {},
  currentFilters = {},
  onFiltersChange = undefined,
  searchTerm = "",
  onResetPeriod = () => {},
  showPeriodToggle = false,
  showSearch = false,
  activePeriod = undefined,
  onPeriodChange = undefined,
  disabled = false,
  onSearchChange,
}) {
  const [rawFilters, setRawFilters] = useState(null);
  const [selected, setSelected] = useState({}); // stores normalized values (for matching)
  const [loading, setLoading] = useState(true);

  // ---- helpers ----
  const normalize = (str) => (str ? String(str).trim().toUpperCase() : "");
  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  // Fetch filters from API
  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        const BASE_URL = process.env.REACT_APP_API_URL;
        const res = await fetch(`${BASE_URL}/get-product-filters/${productId}`);
        const json = await res.json();
        if (mounted) setRawFilters(json.filters || {});
      } catch (e) {
        console.error("Error fetching filters:", e);
        if (mounted) setRawFilters({});
      } finally {
        if (mounted) setLoading(false);
      }
    }
    if (productId) load();
    else setLoading(false);
    return () => (mounted = false);
  }, [productId]);

  // Build normalized option lists AND maps to original/raw labels
  const {
    simpleLists,
    hierarchyOrder,
    hierarchyValues,
    parentMap,
    parentLevelOf,
    displayMap, // displayMap[level][NORMALIZED] => original raw label
  } = useMemo(() => {
    if (!rawFilters) {
      return {
        simpleLists: {},
        hierarchyOrder: [],
        hierarchyValues: {},
        parentMap: {},
        parentLevelOf: {},
        displayMap: {},
      };
    }

    const simpleLists = {};
    const hierarchyOrder = [];
    const hierarchyValues = {};
    const parentMap = {};
    const parentLevelOf = {};
    const displayMap = {}; // level ->s { NORM: raw }

    const ensureLevel = (key) => {
      if (!hierarchyValues[key]) hierarchyValues[key] = new Set();
      if (!parentMap[key]) parentMap[key] = {};
      if (!displayMap[key]) displayMap[key] = {};
      if (!hierarchyOrder.includes(key)) hierarchyOrder.push(key);
    };

     const addToDisplayMap = (level, raw) => {
      const norm = normalize(raw);
      displayMap[level] = displayMap[level] || {};
      if (!(norm in displayMap[level])) displayMap[level][norm] = raw;
    };

    const traverse = (node, currentLevel, parentName) => {
      if (!node || !node.name) return;
      ensureLevel(currentLevel);

      addToDisplayMap(currentLevel, node.name);
      hierarchyValues[currentLevel].add(normalize(node.name));

      if (parentName) {
        const childNorm = normalize(node.name);
        parentMap[currentLevel][childNorm] =
          parentMap[currentLevel][childNorm] || new Set();
        parentMap[currentLevel][childNorm].add(normalize(parentName));
      }

      for (const [childKey, childVal] of Object.entries(node)) {
        if (childKey === "name") continue;

        // child is array of objects (next level)
        if (Array.isArray(childVal) && childVal[0] && typeof childVal[0] === "object" && "name" in childVal[0]) {
          parentLevelOf[childKey] = currentLevel;
          ensureLevel(childKey);
          childVal.forEach((childItem) => traverse(childItem, childKey, node.name));
        }

        // child is array of strings (leaf)
        if (Array.isArray(childVal) && typeof childVal[0] === "string") {
          ensureLevel(childKey);
          parentLevelOf[childKey] = currentLevel;
          childVal.forEach((str) => {
            addToDisplayMap(childKey, str);
            hierarchyValues[childKey].add(normalize(str));
            parentMap[childKey][normalize(str)] =
              parentMap[childKey][normalize(str)] || new Set();
            parentMap[childKey][normalize(str)].add(normalize(node.name));
          });
        }
      }
    };

    // simple lists (RulePhase etc.) — keep normalized list and displayMap
    for (const [k, v] of Object.entries(rawFilters)) {
      if (Array.isArray(v) && typeof v[0] === "string") {
        simpleLists[k] = [...new Set(v.map((x) => normalize(x)))];
        displayMap[k] = displayMap[k] || {};
        v.forEach((raw) => {
          displayMap[k][normalize(raw)] = raw;
        });
      }
    }

    // hierarchy root parse
    const root = rawFilters.hierarchy_filters;
    if (root) {
      for (const [rootKey, arr] of Object.entries(root)) {
        ensureLevel(rootKey);
        arr.forEach((node) => traverse(node, rootKey, null));
      }
    }

    // convert sets in hierarchyValues to arrays (normalized)
    Object.keys(hierarchyValues).forEach((k) => {
      hierarchyValues[k] = [...hierarchyValues[k]];
    });

    return {
      simpleLists,
      hierarchyOrder,
      hierarchyValues,
      parentMap,
      parentLevelOf,
      displayMap,
    };
  }, [rawFilters]);

  // Sync currentFilters -> UI selected (but smartly, case-insensitive keys)
  useEffect(() => {
    if (!rawFilters) return;

    // Build list of component level keys we manage
    const levels = [...hierarchyOrder, ...Object.keys(simpleLists)];

    const nextSelected = {};

    // For each key/value in currentFilters, find matching level (case-insensitive)
    Object.entries(currentFilters || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "") return;
      const matchedLevel = levels.find((lv) => lv.toLowerCase() === String(k).toLowerCase());
      if (matchedLevel) {
        // find normalized form of v (if displayMap has mapping)
        const normV = normalize(v);
        // if displayMap contains this normalized value, use it; otherwise still set normV
        nextSelected[matchedLevel] = normV;
      }
    });

    // Only setSelected if different (prevent overwriting while user interacts)
    if (JSON.stringify(nextSelected) !== JSON.stringify(selected)) {
      setSelected(nextSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilters, rawFilters, hierarchyOrder, JSON.stringify(simpleLists)]);

  // Helper: convert normalized selected -> raw values for sending to parent
  const buildOutgoingFilters = (normSelected) => {
    const outgoing = {};
    Object.entries(normSelected || {}).forEach(([level, normVal]) => {
      if (!normVal) return;
      const raw = (displayMap[level] && displayMap[level][normVal]) || normVal;
      outgoing[level] = raw;
    });
    return outgoing;
  };

  // When user updates a selection in UI
const updateSelection = (key, valueNorm) => {
  setSelected((prev) => {
    const next = { ...prev, [key]: valueNorm };
    Object.entries(parentLevelOf).forEach(([child, parent]) => {
      if (parent === key) next[child] = "";
    });

   
    const autoSelectChildren = (parentKey) => {
      Object.entries(parentLevelOf).forEach(([childLevel, parentLevel]) => {
        if (parentLevel === parentKey) {
          const all = hierarchyValues[childLevel] || [];
          const parentNorm = next[parentKey];
          
          if (!parentNorm) return;

          const valid = [];
          for (const childNorm of all) {
            const parents = parentMap[childLevel]?.[childNorm];
            if (parents && parents.has(parentNorm)) {
              valid.push(childNorm);
            }
          }

          if (valid.length === 1) {
            next[childLevel] = valid[0];
            autoSelectChildren(childLevel);
          }
        }
      });
    };

    autoSelectChildren(key);

    const outgoing = buildOutgoingFilters(next);
    onFiltersChange && onFiltersChange(outgoing);

    return next;
  });
};

  // Helper to create list for select rendering: returns [{ norm, raw }]
  const makeNormalizedList = (arr = [], level) => {
    if (!arr) return [];
    // arr may already be normalized list (for simpleLists) or raw from hierarchyValues
    // prefer to use displayMap[level] if available
    const set = new Set();
    const out = [];

    // if arr contains normalized strings (like from hierarchyValues), use them
    arr.forEach((item) => {
      const norm = normalize(item);
      if (!set.has(norm)) {
        set.add(norm);
        const raw = (displayMap[level] && displayMap[level][norm]) || item;
        out.push({ norm, raw });
      }
    });

    return out;
  };

  // Render dropdown keeping your exact classNames
  const renderDropdown = (level, options = []) => {
    const list = makeNormalizedList(options, level);

    return (
      <select
        value={selected[level] || ""}
        onChange={(e) => {
          const val = e.target.value;
          updateSelection(level, val);
        }}
        className="border border-gray-300 rounded px-2 py-1 h-[40px]"
      >
        <option value="">{`Select ${capitalize(level)}`}</option>
        {list.map(({ norm, raw }) => (
          <option key={norm} value={norm}>
            {raw}
          </option>
        ))}
      </select>
    );
  };

  const handleReset = () => {
    setSelected({});
    onFiltersChange && onFiltersChange({});
    onSearchChange && onSearchChange("");
    onResetPeriod && onResetPeriod();
  };

  const getFilteredValues = (level) => {
    // level exists in hierarchyValues as normalized array
    const all = hierarchyValues[level] || [];

    const parent = parentLevelOf[level];
    if (!parent) return all;

    const parentNorm = selected[parent];
    if (!parentNorm) return all;

    const valid = [];
    for (const childNorm of all) {
      const parents = parentMap[level]?.[childNorm];
      if (parents && parents.has(parentNorm)) valid.push(childNorm);
    }
    return valid;
  };

  if (loading) return <div>Loading filters...</div>;

  return (
    <div className="flex items-start justify-between gap-4 mb-8">
      <div
        className="filters-search-panel grid gap-5 flex-1"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(130px, 130px))" }}
      >
        {/* Hierarchy Filters first */}
        {hierarchyOrder.map((level) => (
          <div key={level} className="filter-items flex flex-col">
            <label className="mb-1">{capitalize(level)}</label>
            {renderDropdown(level, getFilteredValues(level))}
          </div>
        ))}

        {/* Simple lists (like RulePhase) */}
        {Object.entries(simpleLists).map(([key, values]) => (
          <div key={key} className="filter-items flex flex-col">
            <label className="mb-1">{capitalize(key)}</label>
            {renderDropdown(key, values)}
          </div>
        ))}

        {showPeriodToggle && (
          <div className="period-toggling flex gap-2 mt-[27px]">
            <button
              className={`curnt-period ${activePeriod === "Current" ? "active" : ""}`}
              onClick={() => onPeriodChange && onPeriodChange("current")}
            >
              Current Period
            </button>

            <button
              className={`curnt-period ${activePeriod === "Previous" ? "active" : ""}`}
              onClick={() => onPeriodChange && onPeriodChange("last")}
            >
              Last Period
            </button>
          </div>
        )}
      </div>

      <div className="flex items-start self-start mt-[47px]">
        <button
          onClick={handleReset}
          className="flex justify-center items-center px-5 py-3 text-gray-700 bg-white border border-gray-300 rounded h-[40px] w-[70px]"
          disabled={disabled}
        >
          Reset
        </button>
      </div>
    </div>
  );
}
